/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import api from './api';
import type {
  ArtifactDeletionImpact,
  ClonedArtifactIdentity,
  DemandSource,
  DemandSourceListFilter,
  DemandSourceListResult,
} from '../types';
import type { TestOutcome } from '../utils/test-diagnosis';
import { matchesValidationFilter } from '../utils/validation';
import { matchesOwnerFilter, ownersOf } from '../utils/demand-source-list-filter';
import type { ResponseMappingPreview } from '../types/response-mapping-preview';

/**
 * What the operator chose to test *as* — the "Test As" control.
 *
 * Mirrors `DemandSourceTestContextSelection` on the server, which has accepted this
 * since the harness gained a session context (#879). The console never sent it, so
 * every test ran against the server's default preset and the operator could see which
 * device was used but not choose it. Same catalog as the session simulator, on purpose:
 * a source proven against one device profile and simulated against another has been
 * tested twice and validated once (#927 · D1).
 */
export interface TestRunContext {
  /**
   * A device id from {@link TestContextCatalog.devices}. A `string` and not `DevicePresetId`: the
   * catalog is what says which ids exist, and an operator-authored context (`#979`) is an id no
   * union of ours can list. The server falls back to its default for an id it does not know.
   */
  deviceType?: string;
  /** A content id from {@link TestContextCatalog.content}. */
  contentId?: string;
}

/**
 * Mirrors `TestContextOrigin` in `apps/admin/src/demand-source-test-context.ts`. Only `system` exists
 * today; an authored context joins as another origin.
 */
export type TestContextOrigin = 'system';

export interface TestContextChoice {
  id: string;
  label: string;
  origin: TestContextOrigin;
}

/**
 * **What a test or a preview can run as** (`#979`), mirroring the server's `TestContextCatalog`.
 * `defaults` is what the rig applies when nothing is chosen, so the picker opens on it without
 * restating it.
 */
export interface TestContextCatalog {
  devices: TestContextChoice[];
  content: TestContextChoice[];
  defaults: TestRunContext;
}

/**
 * What a test or a preview actually resolved against, echoed by the server (`AppliedTestContext`).
 * Every part is optional here because the console reads it off an untyped body.
 */
export interface AppliedTestContext {
  device?: { type?: string; name?: string };
  content?: { id?: string; title?: string };
  /**
   * The address the request carried (#927). Rendered because the operator could otherwise only
   * discover a documentation IP by reading the outbound request, and until they did, a
   * harness-caused no-bid read as a verdict on their source.
   */
  ip?: { address?: string; routable?: boolean; note?: string };
  /**
   * The pod the request asked for (#1011): "3 ads parsed" reads differently against a 3-ad pod and
   * a 7-ad one.
   */
  pod?: { maxAds?: number; maxDuration?: number };
}

/** The applied context a test or preview result carries, if any. */
export function readAppliedTestContext(result: { body?: unknown } | null | undefined): AppliedTestContext | null {
  const body = result?.body;
  if (body == null || typeof body !== 'object') return null;
  const applied = (body as { testContext?: unknown }).testContext;
  return applied != null && typeof applied === 'object' ? (applied as AppliedTestContext) : null;
}

interface BackendDemandSource extends Omit<Partial<DemandSource>, 'params' | 'method'> {
  demandSourceId?: string;
  /** Looser than the mirror's: the wire has carried params as a JSON string. */
  params?: Record<string, unknown> | string;
  queryParams?: Record<string, unknown> | string;
  fields?: Record<string, unknown> | Array<Record<string, unknown>>;
  endpoint?: string;
  method?: string;
  ownerId?: string;
  createdBy?: string;
  visibility?: 'public' | 'private';
}

export interface ParsedAdTagParameter {
  name: string;
  value: string;
  isMacro: boolean;
  isNumeric: boolean;
  type: 'static' | 'dynamic';
  description?: string;
  mappingSource?: string;
  optional?: boolean;
  /**
   * The literal found in the pasted tag, kept as provenance rather than as a value
   * (#952). Absent for identity and consent parameters, where the sample belongs to
   * whoever produced it.
   */
  sampleValue?: string;
  /** Where retained, the value sent if context resolution finds nothing. */
  defaultValue?: string | number;
  /**
   * How the row came to be, as the parser stamped it (R4b, `#1001`). `unknown` rather than
   * `ValueOrigin` because it crosses the wire: the reader is `readValueOrigin`, which also takes the
   * bare word a parser from before `#1001` sends.
   */
  provenance?: unknown;
}

export interface ParsedAdTagResult {
  demandSource: {
    name?: string;
    endpoint?: string;
    url?: string;
    timeout?: number;
    description?: string;
    method?: string;
    params?: Record<string, unknown> | string;
    queryParams?: Record<string, unknown> | string;
    fields?: Record<string, unknown> | Array<Record<string, unknown>>;
  };
  detectedParameters?: ParsedAdTagParameter[];
  /** The template that claimed the pasted tag's hostname — the artifact (`TM2`, `#1169`). */
  template: DemandSource | null;
  /**
   * What the parse did to the operator's sample — a discarded consent string, a
   * literal that looks like someone's IP under a name we don't recognize (#952).
   * These describe an action already taken, so they must be shown: a silent
   * reclassification is the same class of defect as the silent freeze it replaced.
   */
  warnings?: string[];
}

/** Tolerate historical `method` spellings on records written before #936. */
function normalizeStoredMethod(raw?: string | null): DemandSource['method'] {
  const normalized = String(raw ?? '')
    .trim()
    .toUpperCase();
  if (normalized === 'ORTB' || normalized === 'OPENRTB' || normalized === 'OPEN-RTB') return 'ORTB';
  if (normalized === 'API' || normalized === 'CUSTOM') return 'API';
  return 'VAST';
}

/**
 * `mapMethodToType` and `mapTypeToMethod` were deleted in #936. Both were lossless
 * bijections over three values, and a lossless translation can only ever *lose*
 * information — which is #891, where a partial patch carrying no protocol derived
 * `'VAST'` and silently moved an OpenRTB source onto the VAST driver. The console
 * now speaks the DTO's `method` with the DTO's values, so there is nothing to map.
 * `normalizeDemandSourceMethod` in libs/dto canonicalises historical spellings at
 * the write boundary.
 */

function normalizeParameters(value?: Record<string, unknown> | string): Record<string, unknown> | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return undefined;
    } catch {
      return undefined;
    }
  }
  return value;
}

function hasParameterContent(value?: Record<string, unknown>): boolean {
  if (!value || typeof value !== 'object') return false;

  const fields = value.fields;
  // `staticParams` is retired (`#1135`) and never sent by the server; still a structure key, so a
  // document carrying an empty one is not read as a parameter named `staticParams`.
  const hasStructuredKeys = fields !== undefined || value.staticParams !== undefined;

  const fieldsCount = Array.isArray(fields)
    ? fields.length
    : fields && typeof fields === 'object'
      ? Object.keys(fields as Record<string, unknown>).length
      : 0;

  if (fieldsCount > 0) return true;
  if (hasStructuredKeys) return false;
  return Object.keys(value).length > 0;
}

function normalizeDemandSource(source: BackendDemandSource): DemandSource {
  const canonicalId = source.id ?? source.demandSourceId;
  const rootParams = source.fields ? { fields: source.fields } : undefined;

  const parameterCandidates: Array<Record<string, unknown> | undefined> = [
    normalizeParameters(source.params),
    normalizeParameters(source.params),
    normalizeParameters(source.queryParams),
    rootParams,
  ];

  const normalizedParameters =
    parameterCandidates.find((candidate) => hasParameterContent(candidate)) ??
    parameterCandidates.find((candidate) => candidate != null);

  return {
    ...(source as DemandSource),
    id: canonicalId as DemandSource['id'],
    params: normalizedParameters,
    // Historical records carry `openrtb`/`OPEN-RTB`/padded values; the write
    // boundary canonicalises going forward, this tolerates what is already stored.
    method: normalizeStoredMethod(source.method),
    endpoint: source.endpoint ?? '',
    // **Owner is not the author** (`#900`), and the owner's name is not stored (`#1510`): the server
    // resolves `ownerName` from `ownerId` on every read and the spread above carries it. `createdBy`
    // stays its own field.
    ownerId: source.ownerId,
    createdBy: source.createdBy,
    visibility: source.visibility,
  };
}

function normalizeSourceName(name?: string): string {
  return String(name ?? '')
    .trim()
    .toLowerCase();
}

function normalizeSourceId(id?: string | number): string | null {
  const normalized = String(id ?? '').trim();
  if (!normalized) return null;
  if (normalized.toLowerCase() === 'undefined') return null;
  if (normalized.toLowerCase() === 'null') return null;
  return normalized;
}

function normalizeSourceUrl(url?: string): string {
  return String(url ?? '')
    .trim()
    .toLowerCase();
}

function assertUniqueDemandSourceName(
  name: string | undefined,
  existing: BackendDemandSource[],
  currentId?: string | number,
  currentUrl?: string,
): void {
  const candidate = normalizeSourceName(name);
  if (!candidate) return;

  const normalizedCurrentId = normalizeSourceId(currentId);
  const normalizedCurrentUrl = normalizeSourceUrl(currentUrl);

  const hasDuplicate = (existing ?? []).some((item) => {
    const itemName = normalizeSourceName(item.name);
    if (!itemName || itemName !== candidate) return false;

    if (normalizedCurrentId) {
      const itemId = normalizeSourceId(item.id ?? item.demandSourceId);
      return itemId !== normalizedCurrentId;
    }

    // Legacy/system sources may not have IDs. In update flows, treat matching
    // URL as the same source to avoid self-collision on unchanged names.
    if (normalizedCurrentUrl) {
      const itemUrl = normalizeSourceUrl(String(item.endpoint ?? item.endpoint ?? ''));
      return itemUrl !== normalizedCurrentUrl;
    }

    return true;
  });

  if (hasDuplicate) {
    throw new Error(`Demand source name "${String(name ?? '').trim()}" already exists.`);
  }
}

export const demandSourceService = {
  /** `viewerId` answers the Owner filter's *Mine*; `null` where the caller has no signed-in viewer. */
  async list(filter?: DemandSourceListFilter, viewerId: string | null = null): Promise<DemandSourceListResult> {
    const { data } = await api.get<BackendDemandSource[]>('/api/demand-sources');
    // Apply client-side filtering/sorting/pagination since the existing API
    // returns a flat array. This can be replaced with server-side queries when
    // the backend supports them.
    let items = (data ?? []).map(normalizeDemandSource);
    // Taken before any filter, so choosing one owner does not remove the others from the choices.
    const owners = ownersOf(items);

    if (filter?.search) {
      const q = filter.search.toLowerCase();
      items = items.filter((s) => s.name.toLowerCase().includes(q) || s.endpoint?.toLowerCase().includes(q));
    }
    if (filter?.method) {
      items = items.filter((s) => s.method === filter.method);
    }
    if (filter?.validation) {
      // The predicate lives in `utils/validation.ts` — one projection, so the filter
      // and the badge can never disagree about what "never filled" means (#897).
      items = items.filter((s) => matchesValidationFilter(s, filter.validation!));
    }
    if (filter?.enabled !== null && filter?.enabled !== undefined) {
      items = items.filter((s) => s.enabled === filter.enabled);
    }
    if (filter?.owner) {
      items = items.filter((s) => matchesOwnerFilter(s, filter.owner, viewerId));
    }

    const sortBy = filter?.sortBy ?? 'name';
    const sortDir = filter?.sortDir ?? 'asc';
    items.sort((a, b) => {
      const aVal = String(a[sortBy] ?? '');
      const bVal = String(b[sortBy] ?? '');
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });

    const page = filter?.page ?? 1;
    const pageSize = filter?.pageSize ?? 20;
    const total = items.length;
    items = items.slice((page - 1) * pageSize, page * pageSize);

    return { items, total, page, pageSize, owners };
  },

  /**
   * **One source or authored template, by id** (`3g`). This fetched the whole list and searched it,
   * and the list excludes templates, so a template could not be opened here at all. The route answers
   * both kinds through the lookup update and delete share, and 404s what this viewer cannot see.
   */
  async get(id: string | number): Promise<DemandSource> {
    const { data } = await api.get<BackendDemandSource | undefined>(
      `/api/demand-sources/${encodeURIComponent(String(id))}`,
    );
    // An empty answer is *not found*, as a 404 is — `toggleEnabled` falls back on exactly that.
    if (!data) throw new Error(`Demand source ${id} not found`);
    return normalizeDemandSource(data);
  },

  /**
   * @param fromTemplateId the template this source was created from (`#1176`). The server turns it
   *   into `origin` after checking the actor may see it; the console never sends `origin` itself.
   */
  async create(source: Partial<DemandSource>, fromTemplateId?: string): Promise<DemandSource> {
    const { data: existing } = await api.get<BackendDemandSource[]>('/api/demand-sources');
    assertUniqueDemandSourceName(source.name, existing ?? []);

    // **No fallback id** (A7, `#1147`). This minted `${slug(name)}-${Date.now()}` when the payload
    // carried none — the surrogate step 1 deleted from the controller, surviving one layer up. It is
    // unreachable from the wizard, which has authored an ID since step 1, and the create endpoint now
    // answers 400 for a blank one; minting here would put that 400 out of reach and let a caller
    // create a source whose identity they do not know. Found 2026-08-24 alongside `#1148`, by asking
    // where an id is *minted* rather than grepping for the generator step 1 happened to name.
    const payload = {
      ...source,
      id: source.id,
      endpoint: source.endpoint,
      method: source.method,
      ownerId: source.ownerId,
      visibility: source.visibility,
      createdBy: source.createdBy,
    };

    // **The act, not the fact** (`#1176`). `origin` is read-only to callers — the server resolves
    // this template reference, checks the actor may see it, and stamps where the copy came from. A
    // body field would be a document claiming its own provenance.
    const { data } = await api.post<BackendDemandSource>('/api/demand-sources/create', payload, {
      params: fromTemplateId ? { fromTemplate: fromTemplateId } : undefined,
    });
    return normalizeDemandSource(data);
  },

  /**
   * Update a demand source. `source` may be a PARTIAL patch — the in-place
   * section editors on the detail page send only the slice they own (#851 E).
   *
   * Derived fields are therefore only added when the field they derive from is
   * actually present. Deriving unconditionally is what corrupted sources under
   * #891: a patch carrying no protocol produced `method: mapTypeToMethod(undefined)`
   * = `'VAST'`, the backend merged it over the stored source, and an OpenRTB source
   * silently switched to the VAST driver while the UI still looked fine. Since #936
   * there is no derivation to get wrong — `method` is either in the patch or absent —
   * but the guard stays: an absent field must not be sent.
   */
  async update(
    id: string | number,
    source: Partial<DemandSource>,
    testEvidence?: DemandSourceTestEvidence,
  ): Promise<DemandSource> {
    const { data: existing } = await api.get<BackendDemandSource[]>('/api/demand-sources');
    assertUniqueDemandSourceName(source.name, existing ?? [], id, source.endpoint);

    const payload: Record<string, unknown> = { ...source };
    // Transient (#994): the server splits this off and never persists it. Sent only
    // when a test actually ran, so an ordinary save carries nothing extra.
    if (testEvidence) payload.testEvidence = testEvidence;
    if (source.endpoint !== undefined) payload.endpoint = source.endpoint;
    if (source.method !== undefined) payload.method = source.method;
    const encodedId = encodeURIComponent(String(id));
    const { data } = await api.put<BackendDemandSource>(`/api/demand-sources/${encodedId}`, payload);
    return normalizeDemandSource(data);
  },

  async delete(id: string | number): Promise<void> {
    await api.delete(`/api/demand-sources/${encodeURIComponent(String(id))}`);
  },

  /**
   * Duplicate a source, extended or independent (`P3a`).
   *
   * **Server-side, and that is structural rather than stylistic.** An extended copy's `parentId`
   * holds the original's `objectId`, and the layer it stores is the difference against the parent's
   * *resolved* document — neither of which a client can compute. A console-side duplicate is what
   * the strategy clones used to do: spread the source, new id, new name, and **no recorded
   * relationship at all**.
   */
  async clone(
    id: string | number,
    copy: ClonedArtifactIdentity,
    /**
     * **The editor's unsaved draft**, where the gesture came from one (`ST12` item 2, `#1358`).
     *
     * Absent from every list caller, which copies what is stored. The dialog states which of the two
     * it is doing ({@link CloneSubject}), so this argument and that sentence are one decision — pass
     * a document here and the caller owes `subject="unsaved"`.
     */
    document?: Partial<DemandSource>,
  ): Promise<DemandSource> {
    const { data } = await api.post<BackendDemandSource>(
      `/api/demand-sources/${encodeURIComponent(String(id))}/clone`,
      // **The copy's identity is authored, not derived** (A7, `#1148`). `id` and `name` come from
      // the duplicate dialog; the server refuses an id that is taken rather than minting past it.
      // `asTemplate` rides on the same object because it is part of *what this copy is*, not a
      // second request (`TM9`, `#1171`).
      { ...copy, ...(document ? { document } : {}) },
    );
    return normalizeDemandSource(data);
  },

  /**
   * What deleting this source would take away from the sources that extend it (`P3a`).
   *
   * The confirm dialog's input, and deliberately not what makes the delete safe: the server
   * detaches every child before deleting, so each keeps every value it was inheriting. This says
   * the *link* is going.
   */
  async deletionImpact(id: string | number): Promise<ArtifactDeletionImpact[]> {
    const { data } = await api.get<{ children?: ArtifactDeletionImpact[] }>(
      `/api/demand-sources/${encodeURIComponent(String(id))}/deletion-impact`,
    );
    return data?.children ?? [];
  },

  async toggleEnabled(
    id: string | number,
    enabled: boolean,
    source?: Pick<DemandSource, 'name' | 'endpoint'>,
  ): Promise<DemandSource> {
    // Some deployed backends validate PUT bodies as full DemandSource DTOs.
    // Load current source and send a full update payload with enabled toggled.
    try {
      const current = await demandSourceService.get(id);
      return demandSourceService.update(id, {
        ...current,
        enabled,
      });
    } catch {
      // Fallback for sources that are no longer fetchable by id only.
      const payload: Record<string, unknown> = { enabled };
      if (source?.name) payload.name = source.name;
      if (source?.endpoint) payload.endpoint = source.endpoint;
      const { data } = await api.put<BackendDemandSource>(
        `/api/demand-sources/${encodeURIComponent(String(id))}`,
        payload,
      );
      return normalizeDemandSource(data);
    }
  },

  /**
   * Parse an ad tag URL into a demand-source shape. `type` is a hint: it decides
   * whether URL-parameter suggestions apply at all (OpenRTB carries everything
   * in the request body, so it gets none). Omit it to let the hostname preset —
   * or the VAST fallback — decide.
   */
  async parseAdTag(adTagUrl: string, method?: DemandSource['method']): Promise<ParsedAdTagResult> {
    const { data } = await api.post('/api/parse-ad-tag', {
      adTagUrl,
      ...(method ? { method } : {}),
    });
    return data;
  },

  /**
   * The vendor **templates** — the artifacts (`#1090`, `D31`; `TM-A`, `#1169`).
   *
   * A separate endpoint from `/api/demand-sources`, which excludes templates: a template is not a
   * source anybody serves from, and one list answering both questions is how one ends up in a pod.
   * They open in the ordinary demand-source editors, because they *are* demand sources.
   *
   * **The wizard's picker reads this too.** It read `/api/ad-server-presets` — a four-content-field
   * projection of these same documents — so applying an OpenRTB template produced a source with no
   * request body. That endpoint is gone; this is the one list.
   */
  async listTemplates(): Promise<DemandSource[]> {
    const { data } = await api.get<BackendDemandSource[]>('/api/demand-source-templates');
    return (data ?? []).map(normalizeDemandSource);
  },

  /**
   * Ephemeral driver-based test (#872) — sends the *current* form config in the
   * body instead of referencing a stored source. Works before a source is saved
   * (wizard create mode) and reflects unsaved edits; persists nothing server-side.
   * Returns the same shape as {@link quickTest}, capture triple (#867) included.
   */
  async quickTestConfig(source: Partial<DemandSource>, context: TestRunContext): Promise<QuickTestResult> {
    const payload = {
      ...source,
      endpoint: source.endpoint,
      method: source.method,
    };
    const { data } = await api.post<QuickTestResult>('/api/test-demand-source', {
      source: payload,
      ...(context ? { context } : {}),
    });
    return data;
  },

  /**
   * Resolve the outbound request **without sending it** (R4a/R4e, #898) — what the
   * row's resolution mark reads.
   *
   * Same endpoint and same driver as {@link quickTestConfig}, with `dryRun`. It is
   * deliberately not a separate endpoint: the preview is only worth anything if it
   * is the request that would actually be sent, and two endpoints are two things to
   * keep in step.
   *
   * Records nothing. A test is a write — it moves readiness — and a preview observes
   * nothing, so the server returns no outcome for it.
   */
  async resolveRequestPreview(source: Partial<DemandSource>, context: TestRunContext): Promise<QuickTestResult> {
    const { data } = await api.post<QuickTestResult>('/api/test-demand-source', {
      source: { ...source, endpoint: source.endpoint, method: source.method },
      dryRun: true,
      ...(context ? { context } : {}),
    });
    return data;
  },

  /**
   * **What the response mapping on screen does with a sample response, per ad** (`#1144`).
   *
   * Its own endpoint, unlike {@link resolveRequestPreview}: that one is the request test with
   * `dryRun`, because both build the same request. This reads a pasted response through the driver's
   * parse, which no test runs. What must stay in step with a test is the rig — the pod and session
   * the driver is handed — and the server builds that in one place for both.
   */
  async previewResponseMapping(
    source: Partial<DemandSource>,
    sample: string,
    targets: readonly string[],
    context: TestRunContext,
  ): Promise<ResponseMappingPreview> {
    const { data } = await api.post<ResponseMappingPreview>('/api/response-mapping-preview', {
      source: { ...source, endpoint: source.endpoint, method: source.method },
      sample,
      targets,
      ...(context ? { context } : {}),
    });
    return data;
  },

  /** The choices a *Test as* picker offers, and the rig's defaults (`#979`). */
  async getTestContextCatalog(): Promise<TestContextCatalog> {
    const { data } = await api.get<TestContextCatalog>('/api/test-contexts');
    return {
      devices: Array.isArray(data?.devices) ? data.devices : [],
      content: Array.isArray(data?.content) ? data.content : [],
      defaults: data?.defaults ?? {},
    };
  },

  async quickTest(
    id: string | number,
    fallbackName: string | undefined,
    context: TestRunContext,
  ): Promise<QuickTestResult> {
    const postTest = async (target: string | number) => {
      const encodedTarget = encodeURIComponent(String(target));
      const { data } = await api.post<QuickTestResult>(
        `/api/test-demand-source/${encodedTarget}`,
        context ? { context } : {},
      );
      return data;
    };

    const first = await postTest(id);
    if (!first.ok && first.status === 404 && fallbackName && String(fallbackName) !== String(id)) {
      return postTest(fallbackName);
    }
    return first;
  },
};

/** Result returned from the quick-test endpoint */
/**
 * Evidence the console replays on save (#994). Both halves are produced by the
 * server and echoed back **unmodified** — `TestOutcome` and the state derivation
 * live in `apps/admin`, so the console cannot author either even if it wanted to.
 * It is a courier, which is what keeps "readiness is server-owned" true.
 */
export interface DemandSourceTestEvidence {
  outcome: unknown;
  fingerprint: unknown;
}

export interface QuickTestResult {
  ok: boolean;
  status: number;
  body: unknown;
  /** Fingerprint of the config this test ran against (#994) — replayed on save. */
  testedFingerprint?: unknown;
  /**
   * The three observations, derived server-side (#897).
   *
   * Present on every test the admin API answers. The console renders it rather
   * than re-deciding "did it fill?", because the same derivation is what gets
   * persisted as the source's readiness — a stored claim cannot be decided by the
   * client that displays it. `deriveTestOutcome` remains for the one case the
   * server cannot report: when the admin API itself never answered.
   */
  outcome?: TestOutcome;
}
